import { useState } from "react";

import { AxiosError } from "axios";
import { ChevronDown, RefreshCw, ShieldAlert } from "lucide-react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";

import type { LucideIcon } from "lucide-react";

const formatErrorDetails = (error: unknown): string | undefined => {
  if (error instanceof AxiosError) {
    const response = error.response;
    const request =
      error.config &&
      `${error.config.method?.toUpperCase()} ${error.config.baseURL || ""}${error.config.url || ""}`;
    const reason =
      typeof response?.data === "string"
        ? response.data
        : response?.data?.error;

    return [
      `${error.code || error.name}: ${error.message}`,
      request,
      response
        ? `HTTP ${response.status} ${response.statusText}`
        : "Request never reached the server",
      response && response.status < 500 ? reason : undefined,
    ]
      .filter(Boolean)
      .join("\n");
  }

  if (error instanceof Error) return `${error.name}: ${error.message}`;

  return error ? String(error) : undefined;
};

interface ErrorCardProps {
  icon: LucideIcon;
  title: string;
  message: string;
  details?: string;
}

const ErrorCard = ({ icon: Icon, title, message, details }: ErrorCardProps) => {
  const [detailsOpen, setDetailsOpen] = useState(false);

  return (
    <div className="fixed inset-0 flex items-center justify-center overflow-y-auto py-8 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <div className="w-full max-w-md mx-4">
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-800 p-8">
          <div className="flex flex-col items-center text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center">
              <Icon className="w-8 h-8 text-amber-600 dark:text-amber-500" />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {title}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {message}
              </p>
            </div>
            {details && (
              <Collapsible
                open={detailsOpen}
                onOpenChange={setDetailsOpen}
                className="w-full"
              >
                <CollapsibleTrigger className="flex items-center gap-1 mx-auto text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      detailsOpen ? "rotate-180" : ""
                    }`}
                  />
                  {detailsOpen ? "Hide error details" : "Show error details"}
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <pre className="mt-3 max-h-48 overflow-auto rounded-md border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 p-3 text-left text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                    {details}
                  </pre>
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const AccessDenied = () => {
  return (
    <ErrorCard
      icon={ShieldAlert}
      title="Access Not Granted"
      message="You don't have the required permissions to access this application. Please contact your team administrator for assistance."
    />
  );
};

type UserProfileErrorProps = {
  error?: unknown;
};

export const UserProfileError = ({ error }: UserProfileErrorProps) => {
  return (
    <ErrorCard
      icon={RefreshCw}
      title="Unable to Load Profile"
      message="Please refresh the page or contact support if the issue persists."
      details={formatErrorDetails(error)}
    />
  );
};
